import { Placement } from 'popper.js';
import { MutableRefObject } from 'react';
import { TextWrapping } from '@decisions/server/DecisionsFramework/Design/Report/TextWrapping';
import { TextAlignment } from '@decisions/server/DecisionsFramework/Design/Report/TextAlignment';

import {
  ReportViewConfiguration,
  AggregationViewInfo,
  LiveUpdateColumnInfo,
  ReportViewerCellColorInfo,
  ReportFormattingColorInfo,
  ReportTextOutputType,
} from '@decisions/server/DecisionsFramework/Design/Report/Service/Comunication';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { NumberFormat, DateTimeFormat } from '@decisions/server/DecisionsFramework/Utilities/Data';
import { LabelAttributes } from '@decisions/server/Decisions/Silverlight/UI/Utilities';
import { StyleProps } from 'components/HeaderPanel';
import { RightSideIndicatorOptions } from '@decisions/server/Silverdark/Components/Page/ReportViewer/RightSideIndicatorOptions';
import { IAddBeforeSavePromises } from 'components/property-editors/PropertyGrid/hooks/useEditComplete';
import {
  CalendarViewMode,
  DataDrillView,
  OptionShowMode,
  ReportGridStyles,
  ReportGridType,
  ReportRowClickActionType,
  SelectedRowsStyle,
  SettingsSaveMode,
  ShowFiltersMode,
  Views,
  ViewSwitchMode,
} from '@decisions/server/Silverdark/Components/Page/ReportViewer';
import { NavigateBehavior, PreviewDisplayMode } from '@decisions/server/Silverdark/Controls/ReportDesigner';
import { ReportGridStore } from './views/grid/ReportGridStore';
import { ReportViewerState } from './ReportViewerStore';
import { SilverPartType } from '@decisions/server/Decisions/Silverlight/UI/Controls/SilverPartType';
import { StoreApi, UseBoundStore } from 'zustand';
import { StoreSubscribeWithSelector, Write } from 'types/zustand';

export interface IReportViewerPropsOld {
  currentView?: IReportViews;
  gridViewProps?: IReportGridContainerProps;
}

export interface ReportGridSettings {
  ReportGridType: ReportGridStyles;
  GridIconType: any;
  IsAlternateRowsBackground: any;
  SelectedRowsStyle: any;
  NewGridActionType: any;
  ActionButtonName: any;
  IsRowNumerationEnable: any;
  ShowSelectedRowIndicator: any;
  RowLeftClickActionType: any;
  RowRightClickActionType: any;
  IsGroupingEnable: any;
  IsSortingEnable: any;
  IsGridStretchModeEnable: any;
  RightIndicatorDisplay: RightSideIndicatorOptions | undefined;
  ShowGroupTitle: ShowGroupTitleReportOptions;
  CanAddColumns: any;
  CanRenderGrid: boolean;
  AllowAdjustingColumnProperties: any;
  ShowReportHeaderConfigurationButton: any;
  ReportRowNumbering: any;
  CanAddMultipleColumns: boolean;
  hideMaximizeButton: boolean;
}

export interface ReportViewerPart {
  overrideDrillDownTitle: boolean;
  drillDownTitle: string;
  overrideDrillDownReportTitle: boolean;
  drillDownReportTitle: string;
  saveGridSelectedRow: boolean;
  silverPartType: SilverPartType;
  displayColumns: number;
  saveSelectedReportMode: SettingsSaveMode;
  matrixMaxRows: any;
  matrixMaxColumns: any;
  filterActionByName: boolean;
  actionNamesToShow: string[];
  actionCategoriesToShow: string[];
  filterActions: boolean;
  childFieldName: any;
  parentFieldName: any;
  allowGrouping: any;
  isHideColumnHeader: boolean | undefined;
  allowFiltering: boolean | undefined;
  usePaging: any;
  resetSelectionOnRefresh?: boolean | undefined;
  intervalBetweenEvents: string;
  autoSelectFirstRow: boolean | undefined;
  gridIconType: any;
  isAlternateRowsBackground: any;
  selectedRowsStyle: SelectedRowsStyle;
  newGridActionType: any;
  actionButtonName: any;
  rowRightClickActionType: any;
  isRowNumerationEnable: any;
  showSelectedRowIndicator: boolean;
  isGroupingEnable: any;
  isSortingEnable: any;
  isGridStretchModeEnable: any;
  rightIndicatorDisplay: any;
  showGroupTitle: ShowGroupTitleReportOptions;
  canAddColumns: null;
  allowMaximize: boolean;
  allowAdjustingColumnProperties: any;
  showReportHeaderConfigurationButton: boolean | undefined;
  rowLeftClickActionType: GridRowClickActionType;
  reportGridStyle: ReportGridStyles;
  gridViewStyle: ReportGridType;
  hideSilverpartHeader: boolean;
  viewSwitchMode: ViewSwitchMode;
  saveGridSettingsMode: SettingsSaveMode;
  saveGridSettingsForAnyFolder: SettingsSaveMode;
  limitMatrixRowsAndColumns: number;
  toolbarMode: boolean | null;
  reportViews: Views;
  gridModeNavigationBehavior: NavigateBehavior;
  showFiltersMode: ShowFiltersMode;
  noDataMessage: string;
  selectedPickReportSource: string[];
  keepSearchOpen: boolean;
  selectedReportViews: string[];
  allowReportSelection: boolean;
  dataDrillView: DataDrillView;
  componentId: string;
  previewDisplayMode: PreviewDisplayMode;
  rootFolderLabel: string;
  showExportButton: OptionShowMode;
  showRefreshButton: OptionShowMode;
  showRTEButton: OptionShowMode;
  showReportSaveButton: OptionShowMode;
  showGroupExpanderMode: OptionShowMode;
  showChartButtonsMode: OptionShowMode;
  showFavoriteButton: OptionShowMode;
  startInMultiSelectMode: boolean;
  gridModeShowLinkOnHover: boolean;
  collapseGroupsOnLoading: boolean;
  showSearch: boolean;
  defaultExcelTemplateId: string;
  showDefaultReportList: boolean;
  defaultReportID: string;
  defaultFolderID: string | null;
  showGroupActionsButton: boolean;
  title: string;
  previewShowActions: boolean;
  noDataMessageCssClass: string | null;
  iconWidth: number;
  iconHeight: number;
  defaultCalendarViewName: string | null;
  disableDrillDown?: boolean;
  autoRefreshInterval: string;
  autoRefresh: boolean;
  getReportIdfromSelectionBus: string;
  selectionBusName: string | null;
  showBusyIndicator: boolean;
  busyIndicatorText: string;
  defaultViewName: string;
  blurbRowLeftClickActionType: ReportRowClickActionType;
  blurbRowRightClickActionType: ReportRowClickActionType;
  calendarViewModes: CalendarViewMode[];
  hideNoActionMenu: boolean;
  rowsPerPage: number;
  isGridViewEnabled: boolean;
  showGroupingOptionsInTextView: boolean;
}

export interface IReportViewerProps extends IReportViewerEvents {
  isDesignTime: boolean;
  reportId: string;
  folderId: string;
  instanceId: string;
  pageContextId?: string;
  rowsHolderStyle?: StyleProps;
  reportViewer: ReportViewerPart;
  gridHolder: HTMLElement;
  autoSelectFirstRow: boolean | undefined;
  maxReportRows?: number;
  hideMaximizeButton?: boolean;
  onError?: (err: string) => void;
}

export interface IReportGridRowAction {
  rowLeftClickActionType: GridRowClickActionType;
  rowRightClickActionType: GridRowClickActionType;
}

export interface IReportViewerEvents {
  onRowSelectionChange: (info: ISelectedRowInfo) => void;
  onGridStoreCreated?: (
    store: UseBoundStore<Write<StoreApi<ReportGridStore>, StoreSubscribeWithSelector<ReportGridStore>>>
  ) => void;
  onStoreCreated?: (
    store: UseBoundStore<Write<StoreApi<ReportViewerState>, StoreSubscribeWithSelector<ReportViewerState>>>
  ) => void;
  onContextMenu?: (info: ISelectedRowInfo) => void;
  showMultiSelectMenu?: (selectedRows: IMultiSelectRowsInfo[]) => void;
  onUpdateRows?: () => void;
  reloadGrid?: boolean;
  rowIds?: string[];
  selectedIds?: string[];
}

export enum IReportViews {
  Grid,
  Hierarchical,
}

//this is generated interface, copied it from MVC please do not change the case for properties
interface AggregatedDataItemInfo {
  Label: string;
  Value: any;
  Navigable: boolean;
  Format: string;
  Color: any;
  UseCustomColor: boolean;
  FormattedValue: string;
}

//this is generated interface, copied it from MVC please do not change the case for properties
interface AggregationGroupInfo {
  PathInfo: string;
  AggregationDataGroup: { Name: string; AggregationDataItem: AggregatedDataItemInfo[] }[];
}

export enum GridRowClickActionType {
  ShowSlider,
  ShowActions,
  DefaultAction,
  NoActions,
}

export interface IActiveCellInfo {
  isEditing: boolean;
  id: string;
  hasValidation: boolean;
  selectLastCell?: boolean;
}

export interface IReportGridCommonProps {
  availableWidth?: number;
  selectedRows?: string[];
  selectAll?: boolean;
  onRowSelectionChange?: (info: ISelectedRowInfo) => void;
  onUpdateSelectedRow?: (rowId: string) => void;
  showGroupActions?: () => void;

  hasRuntimeFilters?: boolean;

  onCellSelectionChange?: (info: ISelectedCellInfo) => void;
  activeCellInfo?: IActiveCellInfo;
  rowsHolderStyle?: StyleProps;
  gridScrollView?: HTMLDivElement;
  onToggleNode?: (row: any, collapsed: boolean) => void;
  onGridScroll?: (e: React.UIEvent<HTMLElement>) => void;
  allRowIds?: string[];
  doNotResetSelectedOnRowSelection?: boolean;
  defaultSelectedRowIds?: string[];
  defaultSelectAll?: boolean;

  headerClassName?: string;
  // onFlowInlineClick?: (info: IFlowInlineFieldInfo) => void;
}

export interface ISelectedRowInfo {
  rowId: string;
  selectionParam?: string;
  event?: React.MouseEvent<Element, MouseEvent>;
  action?: { entityId: string; entityType: string };
  doNotOpenDetailLayer?: boolean;
  lastSelectedRow?: number;
  keyField?: string;
  onlyTriggerSelectionChange?: boolean;
}

export interface ISelectedCellInfo {
  id: string;
  editable: boolean;
  isValid?: boolean;
  startInEditingMode?: boolean;
  selectLastCell?: boolean;
}

export interface IMultiSelectionChangeInfo {
  rowId?: string;
  action?: { entityId: string; entityType: string };
  element?: Element;
  selectAll?: boolean;
  isShiftKey?: boolean;
  keyField?: string;
  isCtrlKey?: boolean;
  selectedRowIds?: string[];
}

export enum ReportGridMode {
  SingleSelect,
  MultiSelect,
  InlineEdit,
  InfoOnly,
  OutputAll,
}

export interface IRunTimeGridProps extends IReportGridContainerProps {
  // getData: (pageIndex?: number) => Promise<GridViewResultData>;
  // getDataForParentRow?: (parentId: string) => Promise<GridViewResultData>;
  // updateData: (
  //   updatedConfiguration: ReportViewConfiguration,
  //   currentPage: number
  // ) => Promise<[GridViewResultData, ReportViewConfiguration | null]>;
  onPageChange?: (updatedPageNumber?: number) => void;
}

export interface IReportGridContainerProps extends IReportFilterActionProps, IReportGridCommonProps {
  // designerContentHolder: Element;

  gridHolder: HTMLElement;
  onContextMenu?: (info: ISelectedRowInfo) => void;
  afterGridRefresh?: (data: IAfterRefreshData) => void;
  updateDataForParentRow?: (parentId: string) => void;
}

export interface IGridNoDataProps {
  class?: string;
  message?: string;
}

export interface IAfterRefreshData {
  hasRows: boolean;
  configuration: ReportViewConfiguration;
  rows: any[];
}

export interface IGridContainerProps
  extends IReportGridContainerProps,
    IReportGridPaginationProps,
    IColumnHeaderCommonProps {
  aggregationGroupInfo?: AggregationGroupInfo[];
  aggSeries?: string[];
  totalAggregation?: AggregationViewInfo[];
  typeColumnName?: string;
  toggleMultiSelection?: (activate: boolean) => void;
  onMultiSelectionRangeUpdate?: (ids: string[]) => void;
  reset?: boolean;
  filterLayerLabels?: IFilterLayerLabels;
}

export interface IColumnHeaderCommonProps {
  gridHolder: HTMLElement;
  headerClassName?: string;
}

export interface ITreeViewCommonProps {
  reset?: boolean;
  onToggleNode?: (row: any, collapsed: boolean) => void;
}

export interface IGridViewProps extends IGridContainerProps, IEditableCellCallbacks {
  cellInfo: IReportGridCellInfo[];
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onMultiSelectChange: (info: IMultiSelectionChangeInfo) => void;
  onRowSelectionChange: (info: ISelectedRowInfo) => void;
  rows: any[];
}

export interface IEditedGridRowInfo {
  rowId: string;
  valuesToUpdate: DataPair[];
}

export interface IMultiSelectRowsInfo {
  rowId: string;
  element?: Element;
  action: { entityId: string; entityType: string };
  keyField: string;
}

export interface IRowProps extends IGridRowCommonProps, IGridRowCellCommonProps {
  isRowHovered: boolean;
  isRowSelected: boolean;
  rowColoring?: ReportViewerCellColorInfo;
  showBackgroundColorForSelection?: boolean;
  isMultiSelectOn?: boolean;
  activeCellInfo?: IActiveCellInfo;
  allowCellSelection?: boolean;
}

export interface IGridRowCommonProps {
  cells: any;
  collapsible?: boolean;
  iscollapsed?: boolean;
  toggleCollapse?: (collapse: boolean) => void;
  nestingLevel?: number;
  rowId: string;
  onTabbing?: (info: ITabbingInfo) => void;
  hasDefaultActionColumn?: boolean;
}

export interface IGridRowContainerProps extends IGridCommonProps, IGridRowCommonProps {
  rowIndex: number;
}

export interface IGridProps extends IGridCommonProps {
  rows: any;
}
export interface ICollapsibleGroup extends IGridCommonProps {
  headerName: string;
  row: any;
  width: number | string;
  headerLeftSpace?: number;
  path?: string;
  onResize?: () => void;
}

export interface IGridCommonProps extends IReportGridCommonProps, IEditableCellCallbacks {
  cellInfo: IReportGridCellInfo[];
  showTitleInGroupHeader?: boolean;
  showGroupActions?: () => void;
  isFirstRow?: boolean;
  startIndex?: number;
  gridHolder: HTMLElement;
  onMultiSelectChange?: (info: IMultiSelectionChangeInfo) => void;
}

export interface IGridEditedCellInfo {
  name: string;
  rowId: string;
  updatedVal?: any;
  formattedValue?: any;
}

export interface IGroupedGridProps extends IGridCommonProps {
  groupedRows: any;
  headerLeftSpace?: number;
  aggregationGroupInfo?: AggregationGroupInfo[];
  aggSeries?: string[];
  path?: string;
}

export enum ShowGroupTitleReportOptions {
  Default = 0,
  Never = 1,
  NotOnSingleGrouping = 2,
  Always = 3,
}

export interface IReportGridPaginationProps {}

export enum GridType {
  FIX,
  STRETCH,
}

export enum SortOrder {
  None,
  Ascending,
  Descending,
}

export interface ICommonCellProps {
  name: string;
  width: number; //this is the specified width
  calculatedWidth: number; //this is the width that has been applied at runtime after resizing it.
  hidden?: boolean;
  alignment?: TextAlignment;
  wrapping?: TextWrapping;
  liveUpdateColumnInfo?: LiveUpdateColumnInfo;
  isLink?: boolean;
  hideText?: boolean;
  showPercentageBar?: boolean;
  bottomBound?: number;
  topBound?: number;
  percentageBarBackgroundColor?: string;
  editable?: boolean;
  fullTypeName?: string;
  numberFormat?: NumberFormat;
  customNumberFormat?: string;
  customValidationMessage?: string;
  dateTimeFormat?: DateTimeFormat;
  overrideFont?: boolean;
  fontAttributes?: LabelAttributes;
  cssClassName?: string;
  dateTimeDisplayFormat?: string;
  allowLineBreak?: boolean;
  allowRawHTMLContent?: boolean;
  decimals?: number;
  isNullable?: boolean;
  dataTypeName?: string;
  sortInfo?: string;
  isResponsive?: boolean;
  isFirstCell?: boolean;
  isLastCell?: boolean;
  isDefaultActionLink?: boolean;
  fieldName: string;
  columnFieldName: string;
}

export interface IFlowInlineFieldInfo {
  flowId: string;
  rowId: string;
  showUI: string;
}

export interface IReportGridColProps extends ICommonCellProps {
  id: string;
  label: string;
  position: number;
  data: string[];
  isResizable: boolean;
  allowSort?: boolean;
  allowFilter?: boolean;
  allowGroup?: boolean;
  allowEdit?: boolean;
}

export interface IEditableCellCallbacks {
  onCellEdit?: (info: IGridEditedCellInfo) => void;
  onCellSave?: () => void;
  onTabbing?: (info: ITabbingInfo) => void;
}

export interface IGridRowCellCommonProps extends IEditableCellCallbacks {
  onDefaultGridActionElementClick?: (element: Element) => void;
  onRowSelectionChange: (event: any, doNotOpendetailLayer?: boolean, onlyTriggerSelectionChange?: boolean) => void;
  onCellSelectionChange?: (info: ISelectedCellInfo) => void;
  // onFlowInlineClick?: (info: IFlowInlineFieldInfo) => void;
}

export interface ITabbingInfo {
  currentId: string;
  goToNext: boolean;
}

export interface ISelectedRowIndicatorProps {
  isSelected?: boolean;

  isBroadcastingSelection?: boolean;
}

export interface IReportRowCellProps extends IReportGridCellInfo, IGridRowCellCommonProps {
  label: string;
  isDefaultGridActionCell?: boolean;
  rowId: string | number;
  gridCellColoring?: ReportViewerCellColorInfo | ReportFormattingColorInfo;
  isActive?: boolean;
  isEditing?: boolean;
  isValid?: boolean;
  id: string;
  firstCellLeftPadding?: number;
  allowSelection?: boolean;
}
export interface IReportGridCellInfo extends ICommonCellProps {}

export interface IReportHeaderProps extends IReportFilterActionProps {
  title?: string;
}

interface IReportCommonProps {
  columnsToDelete?: string[];
  totalRow?: number;

  hasRuntimeFilters?: boolean;

  label?: string;
}

export interface IColumnsForGrid {
  id: string;
  name: string;
  fieldName: string;
  label: string;
  position: number;
  width: number;
  allowSort?: boolean;
  allowFilter?: boolean;
  allowGroup?: boolean;
  allowEdit?: boolean;
}

export enum ColumnPosition {
  LEFT,
  RIGHT,
}

export interface IReportFilterActionProps extends IReportGridFilterData, IReportCommonProps, IFilterActionCommon {
  rows?: any[]; //the row must be object
}

export interface IFilterLayerProps
  extends IReportGroupingProps,
    IReportSortingProps,
    IReportColumnFiltersProps,
    IReportColumnsEditorProps,
    IFilterActionCommon,
    IReportRuntimeFilterProps {
  isOpen: boolean;
  sourceRef: MutableRefObject<HTMLElement | null>;
  onClose: () => void;
  closeFilterPanel: () => void;
  className?: string;
  showArrow?: boolean;
  placement?: Placement;
  maxHeight?: number;
}

export interface IFilterActionCommon {
  // onReset?: () => void;
  // onCancel?: () => void;
  // onSave?: (updatedData: IUpdatedGridConfiguration) => void;
  // onColumnAdd?: (info: IRuntimeColumn[]) => void;

  selectedRows?: string[];
  selectAll?: boolean;
  availableColumns?: any;
  // onFilterReset?: () => void;
  gridHolder: HTMLElement;
  filterLayerLabels?: IFilterLayerLabels;
}

export interface IFilterLayerLabels {
  searchLabel: string;
  sortByLabel: string;
  groupByLabel: string;
  multiSelectLabel: string;
  columnsLabel: string;
  rowsLabel: string;
  closeLabel: string;
  resetSettingsLabel: string;
  applyLabel: string;
  widthUnitLabel: string;
}

export interface IUpdatedGridConfiguration {
  groupingfields: string[];
  sortingfields: string[];
  filterInfo: FilterInfo[];
  columns: IReportGridColProps[];
  columnsToDelete: string[];
  isMultiSelectOn?: boolean;
  selectAll?: boolean;
  hasRuntimeFiltersChanged: boolean;
  updatedColumns: string[];
}

export interface IColumnNewPositionInfo {
  columnId: string;
  columnWidth: number;
}

export interface IReportGroupingProps extends IReportCommonProps {
  onGroupingChange?: (newGrouping: string[]) => void;
  groupBy?: string[];
}

export interface IReportRuntimeFilterProps extends IReportCommonProps, IAddBeforeSavePromises {
  editingContextId?: string;
  onFilterReset?: () => void;
  onFIlterClear?: () => void;
}

export interface IReportColumnsEditorProps extends IReportCommonProps {
  onColumnDelete?: (columnId: string, columnName: string) => void;

  onColumnsPositionChanged?: (columnsWithNewPositions: IReportGridColProps[]) => void;
  onColumnsWidthChanged?: (columnId: string, newWidth: number) => void;
  onColumnAdd?: (info: IRuntimeColumn[]) => void;
  availableColumns?: any;
  onWidthUnitChange?: (usePercentage: boolean) => void;
  onColumnEditComplete?: (editingId: string, columnId: string) => void;
  widthAndUnitLabel?: string;
}

export interface IReportGridAddColumnsProps {
  isOpen: boolean;
  sourceRef: any;
  onClose: () => void;
  availableColumns?: any;
  onColumnAdd?: (info: IRuntimeColumn[]) => void;
  canAddMultipleColumns?: boolean;
}

export interface IReportSortingProps extends IReportCommonProps {
  onSortingChange?: (newSorting: string[]) => void;
  sortBy?: string[];
  groupBy?: string[];
}

export interface IReportFilteringProps extends IReportCommonProps, IReportGridFilterData {
  onFilterChange?: (filterInfo: updatedFilterInfo) => void;
  onFilterRemove?: (columnName: string) => void;
  currentFilterInfo: FilterInfo;
}

export interface IReportColumnFiltersProps extends IReportCommonProps, IReportGridFilterData {
  onFilterChange?: (filterInfo: FilterInfo[]) => void;
}

export interface updatedFilterInfo extends FilterInfo {
  oldColumnName: string;
}

export interface FilterInfo {
  columnName: string;
  keyword: string;
  id?: string;
}

export interface IReportGridFilterData {
  allFilters?: FilterInfo[];
}

export interface IReportComboBoxListProps extends IFilterLayerComboCommon {
  comboBoxs: {
    comboBox: IReportComboboxListOptions;
    comboBoxOptions: IReportComboboxListOptions[];
  }[];
}

export interface IFilterLayerComboCommon {
  onChange?: (value: string) => void;
  onClear?: (id: string) => boolean;
  onChosen: (chosenValue: string, oldValue: string, chosenLabel?: string) => void;
  hideClear?: boolean;
}

export interface IReportComboboxListOptions {
  id: string;
  name: string;
  label: string;
  isDisabled?: boolean;
  helpMessage?: string;
}

export interface IFilterLayerToggleButtonProps {
  isOn?: boolean;
  onChange: (isOn: boolean) => void;
  text?: string;
  onText: string;
  offText: string;
  width?: number; //the default width is 98px that comes from css
}

export interface IGridHeaderProps {
  columns: IReportGridColProps[];
  isMultiSelectOn?: boolean;
  selectAll?: boolean;
  onMultiSelectChange?: (info: IMultiSelectionChangeInfo) => void;
  cellInfo: IReportGridCellInfo[];
}

export interface ICellValidationInfo {
  dataTypeName: string;
  numberFormat?: NumberFormat;
  customNumberFormat?: string;
  isDateTimePicker: boolean;
  dateTimeDisplayFormat?: string;
  decimals?: number;
  isNullable?: boolean;
}

export interface IReportCellEditorProps extends IReportRowCellProps {
  value: any; //it could be any type
  validationInfo: ICellValidationInfo;
}

export interface IRuntimeColumn {
  label: string;
  fieldName: string;
  isCalculated: boolean;
}

export interface IAddColumnItem extends IAddColumnCommon {
  headerName: string;
  columns: IRuntimeColumn[];
  pickedColumns: IRuntimeColumn[];
}

export interface IRuntimeColumnProps extends IAddColumnCommon {
  column: IRuntimeColumn;
  isChecked: boolean;
}

export interface IAddColumnCommon {
  canAddMultipleColumns?: boolean;
  onPicked: (info: IRuntimeColumn) => void;
}

export interface ReportHeaderViewItem {
  className: string;
  title: string;
  viewName: string;
  viewType: ReportTextOutputType;
}
